import { useState, useMemo, useEffect, useRef } from 'react';
import { Transaction } from '../types';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  Calendar as CalendarIcon, 
  CheckCircle2, 
  Clock, 
  MessageSquare, 
  DollarSign, 
  CalendarDays, 
  Search, 
  AlertCircle, 
  ListTodo, 
  Loader2,
  CalendarCheck,
  Plus,
  User,
  Check,
  X
} from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, getVenezuelaTodayStr } from '../utils/format';
import { api } from '../lib/axios';

export interface ServerClient {
  name: string;
  phone: string | null;
  document: string | null;
  pending_debt: number | string | null;
  total_transactions: number;
}

interface PaymentCalendarProps {
  transactions: Transaction[];
  onRegisterPayment?: (id: string, amount: number, date: string) => Promise<void>;
  onToggleStatus?: (id: string) => Promise<void>;
  isLoading?: boolean;
}

export interface CalendarEvent {
  id: string;
  type: 'vencimiento';
  date: string; // YYYY-MM-DD
  clientName: string;
  phone?: string;
  transactionId?: string;
  amount?: number;
  status: 'pending' | 'completed';
  notes?: string;
  time?: string;
}

type ViewMode = 'mes' | 'semana' | 'agenda';

export function PaymentCalendar({ transactions, onRegisterPayment, isLoading = false }: PaymentCalendarProps) {
  const { toast } = useUI();
  const { businessId, isAdmin } = useAuth();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDateStr, setSelectedDateStr] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('mes');

  // Filter States: ONLY CUOTAS & VENCIDAS
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyOverdue, setShowOnlyOverdue] = useState(false);
  const [showCompleted, setShowCompleted] = useState(true);

  // Events stored in state and localStorage
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [calendarSyncTrigger, setCalendarSyncTrigger] = useState(0);

  useEffect(() => {
    const handleSync = () => {
      setCalendarSyncTrigger(prev => prev + 1);
    };
    window.addEventListener('storage', handleSync);
    window.addEventListener('calendar-events-updated', handleSync);
    return () => {
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('calendar-events-updated', handleSync);
    };
  }, []);

  // Modals state
  const [isAddCobroOpen, setIsAddCobroOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isRegisterPaymentOpen, setIsRegisterPaymentOpen] = useState(false);

  // Server-side Client Search state
  const [serverClients, setServerClients] = useState<ServerClient[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  // Drag and Drop & active item
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  // Form states
  const [newCobro, setNewCobro] = useState<{
    clientName: string;
    date: string;
    time: string;
    amount: string;
    notes: string;
  }>({
    clientName: '',
    date: '',
    time: '09:00',
    amount: '',
    notes: ''
  });

  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');

  // Close client dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setIsClientDropdownOpen(false);
      }
    }
    if (isClientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClientDropdownOpen]);

  // Close modals or day bottom sheet on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (isAddCobroOpen) setIsAddCobroOpen(false);
        else if (isRescheduleOpen) setIsRescheduleOpen(false);
        else if (isRegisterPaymentOpen) setIsRegisterPaymentOpen(false);
        else if (selectedDateStr) setSelectedDateStr(null);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAddCobroOpen, isRescheduleOpen, isRegisterPaymentOpen, selectedDateStr]);

  // Fetch clients from server with debounce (250ms)
  useEffect(() => {
    if (!isAddCobroOpen) return;

    let isMounted = true;
    setIsLoadingClients(true);

    const timer = setTimeout(async () => {
      try {
        const response = await api.get('/tenant/clients/search', {
          params: {
            query: clientSearchTerm.trim(),
            limit: 25,
          },
        });
        if (isMounted && response.data?.data) {
          setServerClients(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching clients from server:', err);
      } finally {
        if (isMounted) setIsLoadingClients(false);
      }
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [isAddCobroOpen, clientSearchTerm]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const formatPrice = (v: number) => formatCurrency(v);

  const prevPeriod = () => {
    if (viewMode === 'mes') {
      setCurrentDate(new Date(year, month - 1, 1));
    } else if (viewMode === 'semana') {
      const d = new Date(currentDate);
      d.setDate(d.getDate() - 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(new Date(year, month - 1, 1));
    }
    setSelectedDateStr(null);
  };
  
  const nextPeriod = () => {
    if (viewMode === 'mes') {
      setCurrentDate(new Date(year, month + 1, 1));
    } else if (viewMode === 'semana') {
      const d = new Date(currentDate);
      d.setDate(d.getDate() + 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(new Date(year, month + 1, 1));
    }
    setSelectedDateStr(null);
  };

  const periodLabel = useMemo(() => {
    if (viewMode === 'mes' || viewMode === 'agenda') {
      return new Date(year, month, 1).toLocaleString('es-ES', { month: 'long', year: 'numeric', timeZone: 'America/Caracas' });
    } else {
      const day = currentDate.getDay();
      const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(currentDate.getFullYear(), currentDate.getMonth(), diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      
      const monStr = monday.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'America/Caracas' });
      const sunStr = sunday.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Caracas' });
      return `Semana: ${monStr} - ${sunStr}`;
    }
  }, [currentDate, viewMode, year, month]);

  // Seed generator (Solo genera el evento ÚNICO de vencimiento / cuota por transacción)
  const generateSeedEvents = (txs: Transaction[]): CalendarEvent[] => {
    const seeded: CalendarEvent[] = [];
    txs.forEach(tx => {
      if (tx.status === 'Cancelado') return;
      const isTxPaid = tx.status === 'Pagado';
      const dueDateStr = tx.dueDate || tx.date;
      const outstanding = tx.amount - (Number(tx.paidAmount) || 0);

      seeded.push({
        id: `venc-${tx.id}`,
        type: 'vencimiento',
        date: dueDateStr,
        clientName: tx.clientName,
        phone: tx.phone,
        transactionId: tx.id,
        amount: isTxPaid ? tx.amount : (outstanding > 0 ? outstanding : tx.amount),
        status: isTxPaid ? 'completed' : 'pending',
        notes: `Cuota de cobro por valor de $${tx.amount.toLocaleString()}`,
        time: '08:00'
      });
    });
    return seeded;
  };

  // Load and sync cuotas with transactions and localStorage
  useEffect(() => {
    const storageKey = `amigable_cobro_events_${businessId || 'default'}`;
    const stored = localStorage.getItem(storageKey);
    let currentEvents: CalendarEvent[] = [];

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Clean legacy non-vencimiento / promises
        currentEvents = Array.isArray(parsed) ? parsed.filter((e: any) => e.type === 'vencimiento') : [];
      } catch (e) {
        currentEvents = [];
      }
    } else {
      currentEvents = generateSeedEvents(transactions);
      localStorage.setItem(storageKey, JSON.stringify(currentEvents));
    }

    let changed = false;

    // Remove events whose transaction no longer exists or is cancelled (keep custom cobros!)
    const existingTxIds = new Set(transactions.map(t => t.id));
    const prunedEvents = currentEvents.filter(evt => {
      if (!evt.transactionId) return true; // Keep custom programmed cobros!
      const tx = transactions.find(t => t.id === evt.transactionId);
      if (tx && tx.status === 'Cancelado') {
        changed = true;
        return false;
      }
      if (!existingTxIds.has(evt.transactionId)) {
        changed = true;
        return false;
      }
      return true;
    });

    // Sync status and outstanding amounts with DB transactions
    const syncedEvents = prunedEvents.map(evt => {
      if (!evt.transactionId) return evt;
      const tx = transactions.find(t => t.id === evt.transactionId);
      if (!tx) return evt;

      let updatedEvt = { ...evt };
      
      const isTxPaid = tx.status === 'Pagado';
      if (isTxPaid && evt.status !== 'completed') {
        changed = true;
        updatedEvt.status = 'completed';
      } else if (!isTxPaid && evt.status === 'completed') {
        changed = true;
        updatedEvt.status = 'pending';
      }
      
      const outstanding = tx.amount - (Number(tx.paidAmount) || 0);
      const expectedAmount = isTxPaid ? tx.amount : (outstanding > 0 ? outstanding : tx.amount);
      if (evt.amount !== expectedAmount) {
        changed = true;
        updatedEvt.amount = expectedAmount;
      }
      
      const dbDueDateStr = tx.dueDate || tx.date;
      if (evt.date !== dbDueDateStr) {
        changed = true;
        updatedEvt.date = dbDueDateStr;
      }

      return updatedEvt;
    });

    // Seed events for newly added transactions that aren't represented yet
    const existingTxVencIds = new Set(syncedEvents.map(e => e.transactionId));
    const newTxs = transactions.filter(t => !existingTxVencIds.has(t.id));
    if (newTxs.length > 0) {
      const newSeeded = generateSeedEvents(newTxs);
      syncedEvents.push(...newSeeded);
      changed = true;
    }

    if (changed || !stored) {
      localStorage.setItem(storageKey, JSON.stringify(syncedEvents));
    }

    setEvents(syncedEvents);
  }, [transactions, businessId, calendarSyncTrigger]);

  const saveEvents = (updated: CalendarEvent[]) => {
    const storageKey = `amigable_cobro_events_${businessId || 'default'}`;
    localStorage.setItem(storageKey, JSON.stringify(updated));
    setEvents(updated);
  };

  const handleDropOnDay = async (evt: CalendarEvent, targetDateStr: string) => {
    if (evt.date === targetDateStr) return;

    const updatedEvents = events.map(e => e.id === evt.id ? { ...e, date: targetDateStr } : e);
    saveEvents(updatedEvents);

    if (evt.transactionId) {
      try {
        await api.put(`/tenant/transactions/${evt.transactionId}`, { due_date: targetDateStr });
        toast(`Fecha de vencimiento de ${evt.clientName} movida al ${targetDateStr}`, 'success');
      } catch (err) {
        toast('Error al actualizar fecha de vencimiento en servidor', 'error');
      }
    } else {
      toast(`Cuota movida al ${targetDateStr}`, 'success');
    }

    setDraggedEvent(null);
  };

  // Filtered Events: only cuotas
  const filteredEvents = useMemo(() => {
    const todayStr = getVenezuelaTodayStr();
    return events.filter(e => {
      // 1. Solo vencidas filter
      if (showOnlyOverdue) {
        const isOverdue = e.status !== 'completed' && e.date < todayStr;
        if (!isOverdue) return false;
      }

      // 2. Text Search Filter
      if (searchQuery && !e.clientName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // 3. Completed Status Filter
      if (!showCompleted && e.status === 'completed') return false;

      return true;
    });
  }, [events, searchQuery, showOnlyOverdue, showCompleted]);

  // Compute monthly statistics based on cuotas in active month
  const monthlyStats = useMemo(() => {
    const todayStr = getVenezuelaTodayStr();
    const monthEvents = events.filter(e => {
      const itemDate = new Date(e.date + 'T12:00:00Z');
      return itemDate.getFullYear() === year && itemDate.getMonth() === month;
    });

    const totalVenc = monthEvents.length;
    const paidVenc = monthEvents.filter(e => e.status === 'completed').length;
    const overdueEvents = monthEvents.filter(e => e.status !== 'completed' && e.date < todayStr);
    const overdueVenc = overdueEvents.length;
    const overdueAmount = overdueEvents.reduce((sum, curr) => sum + (curr.amount || 0), 0);

    const pendingEvents = monthEvents.filter(e => e.status !== 'completed');
    const totalPendingAmount = pendingEvents.reduce((sum, curr) => sum + (curr.amount || 0), 0);
    const collectionEfficiency = totalVenc > 0 ? Math.round((paidVenc / totalVenc) * 100) : 0;

    return {
      totalVenc,
      paidVenc,
      overdueVenc,
      overdueAmount,
      totalPendingAmount,
      collectionEfficiency,
    };
  }, [events, year, month]);

  // Memoize filtered events mapped by date YYYY-MM-DD
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    filteredEvents.forEach(evt => {
      const d = evt.date;
      if (!map.has(d)) map.set(d, []);
      map.get(d)!.push(evt);
    });
    return map;
  }, [filteredEvents]);

  // Weekly view dates (Mon - Sun)
  const weekDates = useMemo(() => {
    const dates = [];
    const baseDate = new Date(currentDate);
    const day = baseDate.getDay();
    const diff = baseDate.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(baseDate.getFullYear(), baseDate.getMonth(), diff);
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, [currentDate]);

  // Agenda list grouped by date (limited to current month)
  const agendaGroups = useMemo(() => {
    const sorted = [...filteredEvents].sort((a, b) => a.date.localeCompare(b.date));
    const groups: { dateStr: string; dateLabel: string; items: CalendarEvent[] }[] = [];
    sorted.forEach(item => {
      const itemDate = new Date(item.date + 'T12:00:00Z');
      if (itemDate.getFullYear() !== year || itemDate.getMonth() !== month) {
        return;
      }
      let existing = groups.find(g => g.dateStr === item.date);
      if (!existing) {
        const dateLabel = new Date(item.date + 'T12:00:00Z').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'America/Caracas' });
        existing = { dateStr: item.date, dateLabel, items: [] };
        groups.push(existing);
      }
      existing.items.push(item);
    });
    return groups;
  }, [filteredEvents, year, month]);

  const selectedDateEvents = selectedDateStr ? (eventsByDate.get(selectedDateStr) || []) : [];

  // Group events for drawer: Vencidas, Al Día (pendientes), Cobradas
  const groupedEvents = useMemo(() => {
    const todayStr = getVenezuelaTodayStr();
    return {
      vencidas: selectedDateEvents.filter(e => e.status !== 'completed' && e.date < todayStr),
      alDia: selectedDateEvents.filter(e => e.status !== 'completed' && e.date >= todayStr),
      cobradas: selectedDateEvents.filter(e => e.status === 'completed'),
    };
  }, [selectedDateEvents]);

  // Styling for badges
  const getEventBadgeStyles = (evt: CalendarEvent) => {
    const base = "text-[9px] md:text-[10px] leading-tight px-2 py-1 rounded-lg font-semibold truncate border flex items-center gap-1.5 transition-all select-none ";
    const todayStr = getVenezuelaTodayStr();
    
    if (evt.status === 'completed') {
      return base + "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/40";
    }
    if (evt.date < todayStr) {
      return base + "bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800 font-bold shadow-2xs ring-1 ring-rose-200 dark:ring-rose-900/40";
    }
    return base + "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/40";
  };

  const getEventIcon = (evt: CalendarEvent) => {
    const todayStr = getVenezuelaTodayStr();
    if (evt.status === 'completed') return <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />;
    if (evt.date < todayStr) return <AlertCircle className="w-3 h-3 text-rose-600 shrink-0" />;
    return <DollarSign className="w-3 h-3 text-amber-600 shrink-0" />;
  };

  // WhatsApp quick notification link
  const triggerWhatsAppSend = (evt: CalendarEvent) => {
    if (!evt.phone) return;
    const cleanPhone = evt.phone.replace(/[^0-9]/g, '');
    const isOverdue = evt.status !== 'completed' && evt.date < getVenezuelaTodayStr();
    const message = isOverdue
      ? `Hola ${evt.clientName}, le recordamos que presenta una cuota vencida de ${formatCurrency(evt.amount || 0)} con fecha ${evt.date}. Por favor contáctenos para su regularización.`
      : `Hola ${evt.clientName}, le recordamos su cuota pendiente de ${formatCurrency(evt.amount || 0)} para el día ${evt.date}.`;
    const url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleOpenRegisterPayment = (evt: CalendarEvent) => {
    if (!onRegisterPayment) {
      toast('No se puede registrar pagos en este rol', 'error');
      return;
    }
    setActiveEvent(evt);
    setPaymentAmount(evt.amount ? String(evt.amount) : '');
    setIsRegisterPaymentOpen(true);
  };

  const submitRegisterPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeEvent || !onRegisterPayment) return;
    
    const amountNum = parseFloat(paymentAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast('Monto inválido', 'error');
      return;
    }

    setIsSubmittingPayment(true);
    try {
      const txId = activeEvent.transactionId;
      if (!txId) {
        toast('El evento no está vinculado a una transacción activa', 'error');
        return;
      }
      
      await onRegisterPayment(txId, amountNum, activeEvent.date);
      
      const tx = transactions.find(t => t.id === txId);
      const currentOutstanding = tx ? tx.amount - (Number(tx.paidAmount) || 0) : 0;
      const willBeCompleted = currentOutstanding <= amountNum;

      if (willBeCompleted) {
        const updated = events.map(evt => {
          if (evt.id === activeEvent.id || evt.transactionId === txId) {
            return { ...evt, status: 'completed' as const };
          }
          return evt;
        });
        saveEvents(updated);
      }
      
      setIsRegisterPaymentOpen(false);
      setActiveEvent(null);
    } catch (err) {
      toast('Error al registrar abono', 'error');
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleOpenReschedule = (evt: CalendarEvent) => {
    setActiveEvent(evt);
    setRescheduleDate(evt.date);
    setRescheduleNotes(evt.notes || '');
    setIsRescheduleOpen(true);
  };

  const handleProgramCobro = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCobro.clientName) {
      toast('Debes seleccionar o ingresar un cliente', 'error');
      return;
    }
    if (!newCobro.date) {
      toast('Debes ingresar una fecha', 'error');
      return;
    }

    const amountNum = parseFloat(newCobro.amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast('El monto a cobrar es obligatorio y debe ser mayor a 0', 'error');
      return;
    }

    const selectedClientObj = serverClients.find(c => c.name === newCobro.clientName);
    const clientTx = transactions.find(t => t.clientName === newCobro.clientName && t.status !== 'Pagado');

    const created: CalendarEvent = {
      id: `custom-cobro-${Date.now()}`,
      type: 'vencimiento',
      date: newCobro.date,
      clientName: newCobro.clientName,
      phone: selectedClientObj?.phone || clientTx?.phone,
      transactionId: clientTx?.id,
      amount: amountNum,
      status: 'pending',
      notes: newCobro.notes || `Cobro programado por valor de $${amountNum.toLocaleString()}`,
      time: newCobro.time || '09:00'
    };

    saveEvents([...events, created]);
    setIsAddCobroOpen(false);
    setNewCobro({
      clientName: '',
      date: selectedDateStr || '',
      time: '09:00',
      amount: '',
      notes: ''
    });
    toast('Cobro programado exitosamente', 'success');
  };

  const submitReschedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeEvent) return;

    if (!rescheduleDate) {
      toast('Por favor selecciona una fecha', 'error');
      return;
    }

    if (activeEvent.transactionId) {
      try {
        await api.put(`/tenant/transactions/${activeEvent.transactionId}`, { due_date: rescheduleDate });
        toast(`Fecha de vencimiento actualizada al ${rescheduleDate}`, 'success');
      } catch (err) {
        toast('Error al actualizar fecha en servidor', 'error');
        return;
      }
    }

    const updated = events.map(evt => {
      if (evt.id === activeEvent.id) {
        return { 
          ...evt, 
          date: rescheduleDate, 
          notes: rescheduleNotes,
        };
      }
      return evt;
    });

    saveEvents(updated);
    setIsRescheduleOpen(false);
    setActiveEvent(null);
  };

  // Render Day Details Content (Desktop Drawer & Mobile Bottom Sheet)
  const renderDayDetailsContent = (isMobile: boolean) => {
    if (!selectedDateStr) return null;

    const dateObj = new Date(selectedDateStr + 'T12:00:00Z');
    const dayName = dateObj.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'America/Caracas' });
    const fullDateText = dateObj.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Caracas' });
    const isToday = selectedDateStr === getVenezuelaTodayStr();

    return (
      <div className="flex flex-col h-full flex-1 min-h-0 overflow-hidden text-left">
        {/* Header */}
        <div className="flex justify-between items-start mb-3 shrink-0">
          <div className="min-w-0 pr-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase font-extrabold tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-md">
                Gestión del Día
              </span>
              {isToday && (
                <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-400">
                  Hoy
                </span>
              )}
            </div>
            <h3 className="text-base font-extrabold text-slate-900 dark:text-slate-100 mt-1 capitalize truncate">
              {dayName}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
              {fullDateText}
            </p>
          </div>

          <button 
            type="button"
            onClick={() => setSelectedDateStr(null)}
            className={
              isMobile 
                ? 'w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 flex items-center justify-center text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 active:scale-95 shadow-2xs cursor-pointer shrink-0 transition-all' 
                : 'p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-slate-450 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer shrink-0'
            }
            title="Cerrar detalles"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Day Summary Cards */}
        {selectedDateEvents.length > 0 && (
          <div className="grid grid-cols-2 gap-2 mb-3 text-[10px] shrink-0">
            <div className="bg-white dark:bg-slate-800/90 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 shadow-2xs">
              <span className="text-slate-450 dark:text-slate-400 uppercase font-bold block text-[9px] tracking-wide">Cobro Pendiente</span>
              <span className="text-xs font-mono font-extrabold text-amber-600 dark:text-amber-400 block mt-0.5">
                {formatPrice(
                  selectedDateEvents
                    .filter(e => e.status !== 'completed')
                    .reduce((acc, curr) => acc + (curr.amount || 0), 0)
                )}
              </span>
            </div>
            <div className="bg-white dark:bg-slate-800/90 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 shadow-2xs">
              <span className="text-slate-450 dark:text-slate-400 uppercase font-bold block text-[9px] tracking-wide">Cobro Efectivo</span>
              <span className="text-xs font-mono font-extrabold text-emerald-600 dark:text-emerald-400 block mt-0.5">
                {formatPrice(
                  selectedDateEvents
                    .filter(e => e.status === 'completed')
                    .reduce((acc, curr) => acc + (curr.amount || 0), 0)
                )}
              </span>
            </div>
          </div>
        )}

        {/* Scroll list */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1 scrollbar-thin">
          {selectedDateEvents.length === 0 ? (
            <div className="text-center p-8 text-slate-400 dark:text-slate-600 text-xs flex flex-col items-center justify-center h-full">
              <CalendarDays className="w-10 h-10 mb-2 opacity-40 text-slate-400" />
              <p className="font-semibold">No hay cuotas programadas para esta fecha.</p>
            </div>
          ) : (
            <>
              {/* 1. CUOTAS VENCIDAS */}
              {groupedEvents.vencidas.length > 0 && (
                <div className="space-y-2">
                  <span className="text-[10px] font-extrabold text-rose-600 dark:text-rose-400 uppercase tracking-wider flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Cuotas Vencidas ({groupedEvents.vencidas.length})
                  </span>
                  {groupedEvents.vencidas.map(evt => (
                    <div key={evt.id} className="bg-rose-50/40 dark:bg-rose-950/20 p-3.5 rounded-xl border border-rose-200 dark:border-rose-900/60 shadow-3xs space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">{evt.clientName}</div>
                          <div className="text-[10px] text-rose-600 dark:text-rose-400 mt-0.5 flex items-center gap-1 font-semibold">
                            <AlertCircle className="w-2.5 h-2.5" />
                            <span>Vencida</span>
                          </div>
                        </div>
                        <span className="text-[9px] px-2 py-0.5 rounded-md font-extrabold uppercase shrink-0 bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
                          Vencida
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center pt-2 border-t border-rose-100 dark:border-rose-900/40">
                        <span className="text-xs font-mono font-extrabold text-rose-700 dark:text-rose-300">
                          {formatPrice(evt.amount || 0)}
                        </span>
                        <div className="flex items-center gap-2">
                          {evt.phone && (
                            <button
                              type="button"
                              onClick={() => triggerWhatsAppSend(evt)}
                              className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg transition-colors cursor-pointer"
                              title="Enviar recordatorio por WhatsApp"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => handleOpenReschedule(evt)}
                              className="px-2 py-1 text-[10px] font-bold text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 dark:text-slate-300 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                              title="Reprogramar fecha de vencimiento"
                            >
                              Reprogramar
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => handleOpenRegisterPayment(evt)}
                              className="px-2.5 py-1 text-[10px] font-bold text-white bg-rose-600 hover:bg-rose-700 active:scale-95 rounded-lg shadow-xs transition-all cursor-pointer"
                            >
                              Registrar Abono
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 2. CUOTAS AL DÍA / PENDIENTES */}
              {groupedEvents.alDia.length > 0 && (
                <div className="space-y-2">
                  <span className="text-[10px] font-extrabold text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Cuotas por Cobrar ({groupedEvents.alDia.length})
                  </span>
                  {groupedEvents.alDia.map(evt => (
                    <div key={evt.id} className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800 shadow-3xs space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{evt.clientName}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1 font-mono">
                            <Clock className="w-2.5 h-2.5" />
                            <span>Vence hoy o próximamente</span>
                          </div>
                        </div>
                        <span className="text-[9px] px-2 py-0.5 rounded-md font-extrabold uppercase shrink-0 bg-amber-100 text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                          Pendiente
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-xs font-mono font-bold text-slate-800 dark:text-slate-200">
                          {formatPrice(evt.amount || 0)}
                        </span>
                        <div className="flex items-center gap-2">
                          {evt.phone && (
                            <button
                              type="button"
                              onClick={() => triggerWhatsAppSend(evt)}
                              className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg transition-colors cursor-pointer"
                              title="Enviar WhatsApp"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => handleOpenReschedule(evt)}
                              className="px-2 py-1 text-[10px] font-bold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:text-slate-400 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                            >
                              Reprogramar
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => handleOpenRegisterPayment(evt)}
                              className="px-2.5 py-1 text-[10px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 active:scale-95 rounded-lg shadow-xs transition-all cursor-pointer"
                            >
                              Registrar Abono
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 3. CUOTAS COBRADAS / PAGADAS */}
              {groupedEvents.cobradas.length > 0 && (
                <div className="space-y-2">
                  <span className="text-[10px] font-extrabold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Cobradas ({groupedEvents.cobradas.length})
                  </span>
                  {groupedEvents.cobradas.map(evt => (
                    <div key={evt.id} className="bg-white dark:bg-slate-800/60 p-3 rounded-xl border border-emerald-100 dark:border-emerald-950/40 shadow-3xs space-y-1.5 opacity-80">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{evt.clientName}</div>
                        </div>
                        <span className="text-[9px] px-2 py-0.5 rounded-md font-extrabold uppercase shrink-0 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-400">
                          Cobrada
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center pt-1.5 border-t border-slate-100 dark:border-slate-800 text-xs">
                        <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                          {formatPrice(evt.amount || 0)}
                        </span>
                        {evt.phone && (
                          <span className="text-[10px] text-slate-400 font-mono">{evt.phone}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Drawer footer button */}
        <div className="pt-3 mt-auto shrink-0 border-t border-slate-200 dark:border-slate-800 flex flex-col gap-2">
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                setNewCobro(prev => ({ ...prev, date: selectedDateStr || getVenezuelaTodayStr() }));
                setIsAddCobroOpen(true);
              }}
              className="w-full btn btn-primary flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold shadow-xs active:scale-[0.98] transition-transform cursor-pointer"
              title="Programar cobro para este día"
            >
              <Plus className="w-4 h-4" /> Programar Cobro
            </button>
          )}

          {isMobile && (
            <button
              type="button"
              onClick={() => setSelectedDateStr(null)}
              className="w-full py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all cursor-pointer"
            >
              <X className="w-3.5 h-3.5" /> Volver al Calendario
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 gap-3 overflow-hidden">

      {/* HEADER CONTROLS & FILTERS */}
      <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-2.5 md:p-3 flex flex-col md:flex-row gap-2.5 justify-between items-center shadow-xs shrink-0">
        
        {/* Search & Event Toggles: ONLY CUOTAS & VENCIDAS */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Input text search */}
          <div className="relative w-full sm:w-60">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              type="text"
              placeholder="Buscar por cliente..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-2 w-full bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-750 rounded-xl text-xs outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-200 transition-all shadow-2xs"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer">✕</button>
            )}
          </div>

          {/* Toggle pill buttons: Cuotas & Vencidas */}
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            <button
              onClick={() => setShowOnlyOverdue(false)}
              className={`px-3 py-1.5 rounded-lg border font-bold flex items-center gap-1.5 cursor-pointer transition-colors ${
                !showOnlyOverdue
                  ? 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800'
                  : 'bg-white dark:bg-slate-850 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              💰 Todas las Cuotas {isLoading ? <Loader2 className="w-3 h-3 animate-spin text-amber-700 dark:text-amber-400 inline ml-0.5" /> : `(${monthlyStats.totalVenc})`}
            </button>
            <button
              onClick={() => setShowOnlyOverdue(true)}
              className={`px-3 py-1.5 rounded-lg border font-bold flex items-center gap-1.5 cursor-pointer transition-colors ${
                showOnlyOverdue
                  ? 'bg-rose-600 text-white border-rose-700 shadow-xs dark:bg-rose-600'
                  : 'bg-white dark:bg-slate-850 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-900/50 hover:bg-rose-50'
              }`}
            >
              <AlertCircle className={`w-3.5 h-3.5 ${showOnlyOverdue ? 'text-white' : 'text-rose-500'}`} />
              Solo Vencidas {isLoading ? <Loader2 className="w-3 h-3 animate-spin text-rose-600 dark:text-rose-400 inline ml-0.5" /> : (monthlyStats.overdueVenc > 0 ? `(${monthlyStats.overdueVenc})` : '')}
            </button>
          </div>

          {/* Show completed checkbox */}
          <label className="flex items-center gap-1.5 ml-2 text-xs font-bold text-slate-500 dark:text-slate-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={() => setShowCompleted(!showCompleted)}
              className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-650 focus:ring-indigo-500 accent-indigo-600"
            />
            Mostrar Completadas
          </label>


        </div>

        {/* View selector: Month, Week, Agenda */}
        <div className="bg-slate-200/60 dark:bg-slate-800 p-1 rounded-xl flex gap-1 self-stretch md:self-auto text-xs font-bold">
          <button
            onClick={() => setViewMode('mes')}
            className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${
              viewMode === 'mes'
                ? 'bg-white dark:bg-slate-700 text-indigo-650 dark:text-indigo-400 shadow-xs'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
            }`}
          >
            Mes
          </button>
          <button
            onClick={() => setViewMode('semana')}
            className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${
              viewMode === 'semana'
                ? 'bg-white dark:bg-slate-700 text-indigo-650 dark:text-indigo-400 shadow-xs'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
            }`}
          >
            Semana
          </button>
          <button
            onClick={() => setViewMode('agenda')}
            className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${
              viewMode === 'agenda'
                ? 'bg-white dark:bg-slate-700 text-indigo-650 dark:text-indigo-400 shadow-xs'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
            }`}
          >
            Agenda
          </button>
        </div>

      </div>

      {/* MAIN LAYOUT WRAPPER */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm dark:bg-slate-900 dark:border-slate-800 overflow-hidden flex flex-col xl:flex-row flex-1 min-h-0 relative">
        
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 z-40 bg-white/75 dark:bg-slate-900/75 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3 transition-opacity animate-fade-in">
            <div className="bg-white dark:bg-slate-850 p-6 rounded-2xl shadow-xl border border-slate-200/90 dark:border-slate-750 flex flex-col items-center gap-3 animate-scale-in">
              <div className="relative flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-3 border-indigo-100 dark:border-indigo-950 border-t-indigo-600 animate-spin" />
                <CalendarIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400 absolute" />
              </div>
              <div className="text-center space-y-0.5">
                <span className="text-sm font-bold text-slate-800 dark:text-slate-100 block">
                  Cargando calendario...
                </span>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  Sincronizando cuotas y compromisos de cobro
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* VIEW CONTAINER */}
        <div className="flex-1 p-3 md:p-4 border-b xl:border-b-0 xl:border-r border-slate-200 dark:border-slate-800 flex flex-col min-h-0 overflow-hidden">
          
          {/* Header Switcher */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2 shrink-0">
            <div className="flex items-center gap-3">
              <h3 className="font-bold text-base md:text-lg text-slate-800 dark:text-slate-100 capitalize flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-slate-400" />
                {periodLabel}
              </h3>
              <div className="flex items-center gap-1">
                <button onClick={prevPeriod} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors cursor-pointer">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={nextPeriod} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors cursor-pointer">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Overdue quick alert pill */}
            {monthlyStats.overdueVenc > 0 && (
              <button
                onClick={() => setShowOnlyOverdue(prev => !prev)}
                className={`px-3 py-1 rounded-xl text-xs font-bold border transition-all flex items-center gap-1.5 cursor-pointer ${
                  showOnlyOverdue
                    ? 'bg-rose-600 text-white border-rose-700 shadow-xs dark:bg-rose-600'
                    : 'bg-rose-50 text-rose-700 border-rose-250 hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/60'
                }`}
                title="Filtrar para ver solo cuotas vencidas"
              >
                <AlertCircle className={`w-3.5 h-3.5 ${showOnlyOverdue ? 'text-white' : 'text-rose-600'}`} />
                <span>{monthlyStats.overdueVenc} {monthlyStats.overdueVenc === 1 ? 'cuota vencida' : 'cuotas vencidas'} ({formatPrice(monthlyStats.overdueAmount)})</span>
              </button>
            )}
          </div>

          {/* RENDERING VIEW: MONTHLY GRID */}
          {viewMode === 'mes' && (
            <>
              <div className="grid grid-cols-7 gap-1 mb-1 shrink-0">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                  <div key={day} className="text-center text-xs font-bold text-slate-450 dark:text-slate-500 py-1">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1 md:gap-1.5 flex-1 min-h-0" style={{ gridTemplateRows: `repeat(${days.length > 35 ? 6 : 5}, minmax(0, 1fr))` }}>
                {days.map((dayNum, i) => {
                  if (dayNum === null) {
                    return <div key={`empty-${i}`} className="h-full border border-transparent" />;
                  }
                  
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                  const dayEvents = eventsByDate.get(dateStr) || [];
                  
                  const isSelected = selectedDateStr === dateStr;
                  const todayStr = getVenezuelaTodayStr();
                  const isToday = dateStr === todayStr;

                  const overdueEvents = dayEvents.filter(e => e.status !== 'completed' && e.date < todayStr);
                  const hasOverdue = overdueEvents.length > 0;

                  // Sort so overdue events appear first
                  const sortedDayEvents = [...dayEvents].sort((a, b) => {
                    const aOver = a.status !== 'completed' && a.date < todayStr ? 1 : 0;
                    const bOver = b.status !== 'completed' && b.date < todayStr ? 1 : 0;
                    return bOver - aOver;
                  });

                  return (
                    <div
                      key={`day-${dayNum}`}
                      onClick={() => setSelectedDateStr(dateStr)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (draggedEvent) {
                          handleDropOnDay(draggedEvent, dateStr);
                        }
                      }}
                      className={`h-full min-h-0 flex flex-col items-stretch justify-start p-1 md:p-1.5 border rounded-xl relative transition-all overflow-hidden cursor-pointer ${
                        isSelected 
                          ? 'border-indigo-650 bg-indigo-50/20 ring-1 ring-indigo-600 dark:bg-indigo-950/10' 
                          : hasOverdue
                          ? 'border-rose-300 bg-rose-50/35 hover:border-rose-400 dark:border-rose-900/60 dark:bg-rose-950/20'
                          : isToday 
                          ? 'border-indigo-200 bg-slate-50/20 hover:border-indigo-300 dark:border-indigo-850 dark:bg-slate-800/20'
                          : 'border-slate-100 dark:border-slate-800/60 hover:border-slate-300 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/40'
                      }`}
                    >
                      {/* Day Number Header */}
                      <div className="flex justify-between items-center mb-0.5 shrink-0">
                        <div className="flex items-center gap-1">
                          <span className={`text-[10px] md:text-xs font-bold ${
                            isToday 
                              ? 'bg-indigo-600 text-white w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center font-bold' 
                              : hasOverdue
                              ? 'text-rose-700 dark:text-rose-400 font-extrabold px-0.5'
                              : 'text-slate-600 dark:text-slate-400 px-0.5'
                          }`}>
                            {dayNum}
                          </span>
                          {hasOverdue && (
                            <span 
                              className="flex items-center gap-0.5 text-[8px] font-extrabold text-rose-700 bg-rose-100 dark:bg-rose-950/70 dark:text-rose-300 px-1 py-0.2 rounded-full border border-rose-250 dark:border-rose-850" 
                              title={`${overdueEvents.length} cuota(s) vencida(s)`}
                            >
                              <AlertCircle className="w-2.5 h-2.5" />
                              {overdueEvents.length}
                            </span>
                          )}
                        </div>
                        {dayEvents.length > 0 && !hasOverdue && (
                          <span className="text-[8px] font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 dark:text-slate-500 px-1 py-0.2 rounded">
                            {dayEvents.length}
                          </span>
                        )}
                      </div>

                      {/* Stack of Event Bars (Visible on Desktop - Max 2 items, no internal scrollbar) */}
                      <div className="hidden md:flex flex-col gap-0.5 overflow-hidden flex-1 min-h-0 select-none">
                        {sortedDayEvents.slice(0, 2).map(evt => {
                          const isOverdue = evt.status !== 'completed' && evt.date < todayStr;
                          return (
                            <div 
                              key={evt.id} 
                              draggable={true}
                              onDragStart={(e) => {
                                e.stopPropagation();
                                setDraggedEvent(evt);
                                e.dataTransfer.setData('text/plain', evt.id);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              className={getEventBadgeStyles(evt) + " cursor-grab active:cursor-grabbing hover:scale-[1.02] shadow-2xs justify-between shrink-0"}
                              title={`${evt.clientName} - ${isOverdue ? 'Cuota Vencida' : 'Cuota de Cobro'} (${formatPrice(evt.amount || 0)})`}
                            >
                              <div className="flex items-center gap-1 min-w-0 truncate">
                                {getEventIcon(evt)}
                                <span className="truncate">{evt.clientName}</span>
                              </div>
                              {evt.amount !== undefined && evt.amount > 0 && (
                                <span className="font-mono text-[8px] font-bold opacity-80 shrink-0 ml-1">
                                  {formatPrice(evt.amount)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                        {dayEvents.length > 2 && (
                          <div className="text-[8.5px] font-bold text-slate-400 dark:text-slate-500 text-center mt-auto font-mono py-0.5 shrink-0">
                            + {dayEvents.length - 2} más
                          </div>
                        )}
                      </div>

                      {/* Thin responsive bars on Mobile */}
                      <div className="flex md:hidden flex-wrap gap-0.5 justify-center mt-auto pb-1">
                        {dayEvents.map(evt => {
                          const isOverdue = evt.status !== 'completed' && evt.date < todayStr;
                          const color = evt.status === 'completed' ? "bg-emerald-500" : isOverdue ? "bg-rose-600" : "bg-amber-500";
                          return <div key={evt.id} className={`h-1.5 w-2.5 rounded-full ${color}`} />;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* RENDERING VIEW: WEEKLY GRID */}
          {viewMode === 'semana' && (
            <div className="grid grid-cols-1 md:grid-cols-7 gap-2 flex-1 min-h-0 overflow-y-auto scrollbar-thin">
              {weekDates.map(date => {
                const dateStr = date.toISOString().substring(0, 10);
                const dayEvents = eventsByDate.get(dateStr) || [];
                const isToday = dateStr === getVenezuelaTodayStr();
                const isSelected = selectedDateStr === dateStr;

                return (
                  <div
                    key={dateStr}
                    onClick={() => setSelectedDateStr(dateStr)}
                    className={`border p-2.5 rounded-xl flex flex-col h-full min-h-[220px] md:min-h-0 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-indigo-650 bg-indigo-50/10 dark:bg-indigo-950/10'
                        : isToday 
                        ? 'border-indigo-200 bg-slate-50/20 dark:border-indigo-850 dark:bg-slate-800/20'
                        : 'border-slate-100 dark:border-slate-800 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/30'
                    }`}
                  >
                    {/* Day Column Header */}
                    <div className="border-b pb-2 mb-3 dark:border-slate-800">
                      <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">
                        {date.toLocaleDateString('es-ES', { weekday: 'short', timeZone: 'America/Caracas' })}
                      </div>
                      <div className={`text-base font-extrabold mt-0.5 ${isToday ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-350'}`}>
                        {date.getDate()}
                      </div>
                    </div>

                    {/* Column Items */}
                    <div className="space-y-2 flex-1 overflow-y-auto no-scrollbar">
                      {dayEvents.length === 0 ? (
                        <span className="text-[10px] text-slate-350 dark:text-slate-650 italic block text-center py-6">Sin cuotas</span>
                      ) : (
                        dayEvents.map(evt => (
                          <div 
                            key={evt.id} 
                            className={getEventBadgeStyles(evt) + " flex-col items-start px-2.5 py-2 whitespace-normal"}
                          >
                            <div className="flex items-center gap-1.5 w-full">
                              {getEventIcon(evt)}
                              <span className="font-mono text-[9px]">{evt.time}</span>
                            </div>
                            <div className="font-bold text-[10px] mt-1 break-words leading-tight">{evt.clientName}</div>
                            {evt.amount !== undefined && (
                              <div className="text-[9px] font-bold font-mono mt-0.5 opacity-80">{formatCurrency(evt.amount)}</div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* RENDERING VIEW: AGENDA LIST */}
          {viewMode === 'agenda' && (
            <div className="space-y-4 flex-1 min-h-0 overflow-y-auto pr-2 scrollbar-thin">
              {agendaGroups.length === 0 ? (
                <div className="text-center py-16 text-slate-400 dark:text-slate-650 flex flex-col items-center justify-center">
                  <ListTodo className="w-12 h-12 mb-3 text-slate-200 dark:text-slate-850" />
                  <p className="text-sm font-bold">No hay cuotas registradas para los filtros seleccionados.</p>
                </div>
              ) : (
                agendaGroups.map(group => (
                  <div key={group.dateStr} className="relative pl-6 border-l-2 border-indigo-100 dark:border-slate-800">
                    <div className={`w-3.5 h-3.5 rounded-full absolute -left-[7px] top-1.5 border-2 border-white dark:border-slate-900 ${
                      group.dateStr === getVenezuelaTodayStr() ? 'bg-indigo-650 ring-2 ring-indigo-150' : 'bg-slate-300 dark:bg-slate-700'
                    }`} />
                    
                    <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 capitalize mb-3">
                      {group.dateLabel}
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {group.items.map(evt => {
                        const isOverdue = evt.status !== 'completed' && evt.date < getVenezuelaTodayStr();
                        return (
                          <div 
                            key={evt.id} 
                            onClick={() => setSelectedDateStr(evt.date)}
                            className="bg-slate-50 hover:bg-slate-100/70 dark:bg-slate-850/50 dark:hover:bg-slate-800 border border-slate-150 dark:border-slate-800 p-3.5 rounded-2xl flex flex-col justify-between shadow-3xs hover:shadow-xs transition-all cursor-pointer"
                          >
                            <div className="flex justify-between items-start gap-2">
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <span className={`w-2 h-2 rounded-full ${
                                    evt.status === 'completed' ? 'bg-emerald-500' : isOverdue ? 'bg-rose-500' : 'bg-amber-500'
                                  }`} />
                                  <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">
                                    {evt.status === 'completed' ? 'Cobrada' : isOverdue ? 'Vencida' : 'Cuota'}
                                  </span>
                                  <span className="font-mono text-[9px] text-slate-400">· {evt.time}</span>
                                </div>
                                <h5 className="text-xs font-extrabold text-slate-800 dark:text-slate-200 mt-1">{evt.clientName}</h5>
                              </div>

                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${
                                evt.status === 'completed'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-400'
                                  : isOverdue
                                  ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/20 dark:text-rose-400'
                                  : 'bg-amber-100 text-amber-800 dark:bg-amber-950/20 dark:text-amber-450'
                              }`}>
                                {evt.status === 'completed' ? 'Cobrada' : isOverdue ? 'Vencida' : 'Pendiente'}
                              </span>
                            </div>

                            <div className="flex justify-between items-center mt-3 pt-2.5 border-t border-slate-200/50 dark:border-slate-850">
                              <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-350">
                                {evt.amount !== undefined ? formatPrice(evt.amount) : 'Sin monto'}
                              </span>

                              <div className="flex items-center gap-2">
                                {evt.status === 'pending' && isAdmin && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenRegisterPayment(evt);
                                    }}
                                    className="text-[10px] font-bold text-indigo-650 hover:underline cursor-pointer"
                                  >
                                    Abonar
                                  </button>
                                )}
                                {evt.phone && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      triggerWhatsAppSend(evt);
                                    }}
                                    className="p-1 text-slate-400 hover:text-emerald-600 transition-colors cursor-pointer"
                                    title="WhatsApp Link"
                                  >
                                    <MessageSquare className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

        </div>

        {/* DESKTOP DETAILS SIDEBAR (Visible only on xl screens) */}
        <div className="hidden xl:flex xl:w-96 bg-slate-50 dark:bg-slate-900/60 p-4 md:p-5 flex-col shrink-0 text-left border-l border-slate-200 dark:border-slate-800 h-full min-h-0 overflow-hidden">
          {selectedDateStr ? (
            renderDayDetailsContent(false)
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 dark:text-slate-650 text-center p-6">
              <CalendarIcon className="w-12 h-12 mb-3 text-slate-200 dark:text-slate-800" />
              <p className="text-sm font-semibold">Selecciona un día para ver el detalle de sus cuotas.</p>
            </div>
          )}
        </div>

      </div>

      {/* MOBILE RESPONSIVE BOTTOM SHEET (< xl) */}
      {selectedDateStr && (
        <div 
          className="xl:hidden fixed inset-0 z-50 bg-slate-950/65 backdrop-blur-xs flex flex-col justify-end animate-fade-in"
          onClick={() => setSelectedDateStr(null)}
        >
          <div 
            className="w-full max-h-[85vh] bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl border-t border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            {/* Drag Handle */}
            <div 
              className="w-full pt-3 pb-1 flex justify-center cursor-pointer active:opacity-60 shrink-0"
              onClick={() => setSelectedDateStr(null)}
              title="Toca para salir"
            >
              <div className="w-12 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700" />
            </div>

            <div className="flex-1 flex flex-col min-h-0 overflow-hidden px-4 pb-4">
              {renderDayDetailsContent(true)}
            </div>
          </div>
        </div>
      )}

      {/* RE-SCHEDULE MODAL */}
      {isRescheduleOpen && activeEvent && (
        <div 
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-fade-in"
          onClick={() => setIsRescheduleOpen(false)}
        >
          <div 
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-slate-200 dark:border-slate-800 animate-scale-in"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                <CalendarCheck className="w-4 h-4 text-indigo-600" /> Reprogramar Cuota: {activeEvent.clientName}
              </h3>
              <button 
                onClick={() => setIsRescheduleOpen(false)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-855 rounded text-slate-400 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={submitReschedule} className="space-y-4 text-xs text-left">
              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-350">Nueva Fecha de Vencimiento</label>
                <input
                  type="date"
                  required
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={rescheduleDate}
                  onChange={e => setRescheduleDate(e.target.value)}
                />
              </div>

              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-350">Motivo / Notas</label>
                <textarea
                  placeholder="Detalles de por qué se reprogramó la cuota..."
                  rows={3}
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={rescheduleNotes}
                  onChange={e => setRescheduleNotes(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsRescheduleOpen(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Confirmar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REGISTER ABONO (PAYMENT) MODAL */}
      {isRegisterPaymentOpen && activeEvent && (
        <div 
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-fade-in"
          onClick={() => setIsRegisterPaymentOpen(false)}
        >
          <div 
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-slate-200 dark:border-slate-800 animate-scale-in"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                <DollarSign className="w-4.5 h-4.5 text-emerald-600" /> Registrar Cobro / Abono
              </h3>
              <button 
                onClick={() => setIsRegisterPaymentOpen(false)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-855 rounded text-slate-400 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={submitRegisterPayment} className="space-y-4 text-xs text-left">
              <div>
                <label className="block font-bold mb-1 text-slate-750 dark:text-slate-300">Cliente</label>
                <div className="font-semibold text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-850 p-2.5 rounded-lg border dark:border-slate-800">
                  {activeEvent.clientName}
                </div>
              </div>

              <div>
                <label className="block font-bold mb-1 text-slate-750 dark:text-slate-300">Monto del Abono ($)</label>
                <input
                  type="number"
                  required
                  min="0.01"
                  step="any"
                  placeholder="Monto..."
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none font-mono focus:ring-2 focus:ring-indigo-500"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsRegisterPaymentOpen(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingPayment}
                  className="btn btn-success flex items-center justify-center gap-2"
                >
                  {isSubmittingPayment && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmittingPayment ? 'Abonando...' : 'Abonar Pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PROGRAMAR COBRO MODAL */}
      {isAddCobroOpen && (
        <div 
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 overflow-y-auto animate-fade-in"
          onClick={() => setIsAddCobroOpen(false)}
        >
          <div 
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-slate-200 dark:border-slate-800 animate-scale-in"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Programar Cobro
              </h3>
              <button 
                onClick={() => setIsAddCobroOpen(false)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-850 rounded text-slate-400 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleProgramCobro} className="space-y-4 text-xs text-left">
              <div ref={clientDropdownRef} className="relative">
                <div className="flex justify-between items-center mb-1">
                  <label className="block font-bold text-slate-700 dark:text-slate-300">Cliente Asignado</label>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Búsqueda en servidor (hasta 25)</span>
                </div>

                {/* Combobox Trigger Button */}
                <div
                  onClick={() => setIsClientDropdownOpen(prev => !prev)}
                  className={`w-full border p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 flex items-center justify-between cursor-pointer transition-all shadow-2xs ${
                    isClientDropdownOpen 
                      ? 'border-indigo-500 ring-2 ring-indigo-500/20' 
                      : 'border-slate-200 dark:border-slate-750 hover:border-slate-300 dark:hover:border-slate-650'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <User className="w-4 h-4 text-slate-400 shrink-0" />
                    {newCobro.clientName ? (
                      <span className="font-bold text-slate-800 dark:text-slate-100 truncate">
                        {newCobro.clientName}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">-- Seleccionar Cliente --</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {newCobro.clientName && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewCobro(prev => ({ ...prev, clientName: '', amount: '' }));
                        }}
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-750 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                        title="Deseleccionar cliente"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isClientDropdownOpen ? 'rotate-180 text-indigo-600' : ''}`} />
                  </div>
                </div>

                {/* Hidden input for HTML5 form validation */}
                <input
                  type="text"
                  required
                  tabIndex={-1}
                  value={newCobro.clientName}
                  onChange={() => {}}
                  className="opacity-0 absolute h-0 w-0 pointer-events-none"
                />

                {/* Dropdown Popover */}
                {isClientDropdownOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-750 rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
                    {/* Live Search Input */}
                    <div className="p-2.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-850/60">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                        <input
                          autoFocus
                          type="text"
                          placeholder="Buscar por nombre, cédula o teléfono..."
                          value={clientSearchTerm}
                          onChange={e => setClientSearchTerm(e.target.value)}
                          className="w-full pl-8 pr-7 py-1.5 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none focus:border-indigo-500 text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                        />
                        {clientSearchTerm && (
                          <button
                            type="button"
                            onClick={() => setClientSearchTerm('')}
                            className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Results list */}
                    <div className="max-h-56 overflow-y-auto scrollbar-thin p-1.5 space-y-1">
                      {isLoadingClients ? (
                        <div className="p-4 flex items-center justify-center gap-2 text-xs text-slate-400">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                          <span>Buscando clientes en servidor...</span>
                        </div>
                      ) : serverClients.length === 0 ? (
                        <div className="p-4 text-center text-xs text-slate-400">
                          No se encontraron clientes para "{clientSearchTerm}".
                        </div>
                      ) : (
                        serverClients.map(c => {
                          const isSelected = newCobro.clientName === c.name;
                          const debtNum = parseFloat(String(c.pending_debt || 0));

                          return (
                            <div
                              key={c.name}
                              onClick={() => {
                                setNewCobro(prev => ({
                                  ...prev,
                                  clientName: c.name,
                                  amount: prev.amount ? prev.amount : (debtNum > 0 ? String(debtNum) : ''),
                                }));
                                setIsClientDropdownOpen(false);
                                setClientSearchTerm('');
                              }}
                              className={`p-2.5 rounded-xl flex items-center justify-between cursor-pointer transition-all text-xs ${
                                isSelected
                                  ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200 font-semibold'
                                  : 'hover:bg-slate-100/80 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-350'
                              }`}
                            >
                              <div className="min-w-0 flex-1 pr-2">
                                <div className="font-bold text-slate-800 dark:text-slate-100 truncate">
                                  {c.name}
                                </div>
                                <div className="text-[10px] text-slate-400 flex items-center gap-2 mt-0.5">
                                  {c.document && <span>{c.document}</span>}
                                  {c.document && c.phone && <span>·</span>}
                                  {c.phone && <span>{c.phone}</span>}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                {debtNum > 0 && (
                                  <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200/60 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/50">
                                    {formatCurrency(debtNum)}
                                  </span>
                                )}
                                {isSelected && <Check className="w-4 h-4 text-indigo-600 shrink-0" />}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">Fecha del Cobro</label>
                  <input
                    type="date"
                    required
                    className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
                    value={newCobro.date}
                    onChange={e => setNewCobro({ ...newCobro, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">Hora</label>
                  <input
                    type="time"
                    className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
                    value={newCobro.time}
                    onChange={e => setNewCobro({ ...newCobro, time: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block font-bold text-slate-700 dark:text-slate-300">Monto a Cobrar ($) *</label>
                  <span className="text-[10px] text-rose-500 font-bold">Obligatorio</span>
                </div>
                <div className="relative">
                  <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="number"
                    required
                    min="0.01"
                    step="any"
                    placeholder="0.00"
                    className="w-full border dark:border-slate-750 pl-9 pr-3 py-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none font-mono font-bold focus:ring-2 focus:ring-indigo-500"
                    value={newCobro.amount}
                    onChange={e => setNewCobro({ ...newCobro, amount: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">Notas / Observaciones</label>
                <textarea
                  placeholder="Detalles específicos del cobro o cuota acordada..."
                  rows={3}
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={newCobro.notes}
                  onChange={e => setNewCobro({ ...newCobro, notes: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsAddCobroOpen(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Guardar Cobro
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
